import { useCallback, useRef, useState, useMemo, Fragment, Ref } from 'react';
import { capitalize, uniqBy } from 'lodash';
import classNames from 'classnames';

import { first } from 'lodash';
import {
  FloatingList,
  useFloating,
  useInteractions,
  useListItem,
  useListNavigation,
  flip,
  offset,
  shift,
  size,
  autoUpdate,
  useClick,
  useDismiss,
  useRole,
  FloatingPortal,
  Placement,
} from '@floating-ui/react';

import {
  FormInputSize,
  SelectOptions,
  isOptionGroup,
  SelectOption,
  Option,
} from '@/components/form/types';
import { useSelectedAccount } from '@/lib/accounts/context';
import { useMetadataContext } from '@/explore/metadata-context';
import { ChatMode, ChatSource } from '@/explore/types';
import { Badge } from '@/components/badge';
import { Icon } from '@/components/icon';
import { Tooltip } from '@/components/tooltip';
import { toTitleCase } from '@/lib/utils/string';
import { useLayoutContext } from '@/components/layout/layout-context';
import { notNil } from '@/lib/utils';

import { getAvailableSources } from '../utils';

import styles, { badgeBgColor, badgeColor } from './chat-settings.module.scss';

const DatabaseSource: Option = {
  label: 'Database',
  value: ChatSource.Database,
  tooltip: 'Let AI answer questions about the data in your connected database',
};
/*
const AccountSource: Option = {
  label: 'Model configuration',
  value: ChatSource.Account,
  tooltip: 'Answer questions about your data models',
};
*/
const NormalMode: Option = {
  label: 'Normal',
  value: 'mode:normal',
  description: 'Fastest and works for most questions',
};
const ResearchMode: Option = {
  label: 'Research',
  value: 'mode:research',
  description: 'Best for complex questions, thinks for much longer',
  badge: 'beta',
};

interface SettingsDropdownProps {
  options: SelectOptions;
  className?: classNames.Argument;
  size?: FormInputSize;
  customLabel?: string;
  value?: string[];
  placement: Placement;
  onChange: (value: string[]) => void;
  onClose: () => void;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

const getSelectedOptionLabel = ({
  value,
  selectedOptionParent,
  options,
}: {
  value?: string | string[];
  selectedOptionParent?: SelectOption;
  options: SelectOptions;
}) => {
  const flatOptions = options.flatMap((option) =>
    isOptionGroup(option) ? option.options : option,
  );
  const selectedOption = Array.isArray(value)
    ? flatOptions.find((option) => value.includes(option.value))
    : flatOptions.find((option) => option.value === value);

  if (Array.isArray(value)) {
    if (value.length > 1) {
      return value.join(', ');
    }
    return selectedOption?.label ?? '';
  }

  return selectedOptionParent !== undefined
    ? `${selectedOption?.label} (${selectedOptionParent.label})`
    : (selectedOption?.label ?? first(flatOptions)?.label ?? '');
};

function SettingsDropdown(props: SettingsDropdownProps) {
  const {
    options,
    value,
    customLabel,
    size: triggerSize,
    placement,
    onChange,
    onClose,
    isOpen,
    onOpenChange,
  } = props;

  const { scrollAreaRef } = useLayoutContext();

  const selectedOptionParent = options.find(
    (option) =>
      isOptionGroup(option) && option.options.some((o) => value?.includes(o.value) ?? false),
  );
  const selectedOptionLabel = useMemo(
    () => getSelectedOptionLabel({ value, options, selectedOptionParent }),
    [value, options, selectedOptionParent],
  );

  const closePanel = useCallback(() => {
    onOpenChange(false);
    onClose();
  }, [onClose, onOpenChange]);

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: (open) => {
      onOpenChange(open);
      if (!open) {
        onClose();
      }
    },
    placement,
    middleware: [
      offset({ mainAxis: 0, crossAxis: -4 }),
      flip({
        boundary: scrollAreaRef?.current ?? undefined,
      }),
      shift({
        padding: 8,
        boundary: scrollAreaRef?.current ?? undefined,
      }),
      size({
        apply({ rects, availableHeight, elements }) {
          Object.assign(elements.floating.style, {
            maxHeight: `${availableHeight}px`,
            minWidth: `${rects.reference.width}px`,
          });
        },
        boundary: scrollAreaRef?.current ?? undefined,
      }),
    ],
    whileElementsMounted: autoUpdate,
  });

  const click = useClick(context);
  const dismiss = useDismiss(context);
  const role = useRole(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss, role]);

  return (
    <div
      className={classNames(styles.settings, props.className, {
        [styles.sizeSmall]: triggerSize === 'small',
        [styles.sizeMedium]: triggerSize === 'regular',
        [styles.sizeLarge]: triggerSize === 'large',
      })}>
      <SettingsTrigger
        label={customLabel ?? selectedOptionLabel}
        onTogglePanel={(state) => onOpenChange(state ?? !isOpen)}
        ref={refs.setReference}
        getReferenceProps={getReferenceProps}
      />
      {isOpen && (
        <FloatingPortal>
          <SettingsPanel
            options={options}
            value={value}
            ref={refs.setFloating}
            style={floatingStyles}
            onChange={onChange}
            onClose={closePanel}
            size={triggerSize}
            getFloatingProps={getFloatingProps}
          />
        </FloatingPortal>
      )}
    </div>
  );
}

type SettingsTriggerProps = {
  ref?: Ref<HTMLButtonElement>;
  label: string;
  onTogglePanel(state?: boolean): void;
  getReferenceProps?: ReturnType<typeof useInteractions>['getReferenceProps'];
};

const SettingsTrigger = ({
  ref,
  label,
  onTogglePanel,
  getReferenceProps,
}: SettingsTriggerProps) => (
  <button
    ref={ref}
    type="button"
    className={styles.trigger}
    title={label}
    onClick={() => onTogglePanel()}
    {...(getReferenceProps ? getReferenceProps() : {})}>
    {label}
    <Icon name="ChevronDown" size={12} />
  </button>
);

interface SettingsPanelProps {
  ref?: Ref<HTMLDivElement>;
  options: SelectOptions;
  value?: string[];
  style?: React.CSSProperties;
  size?: FormInputSize;
  onChange: (value: string[]) => void;
  onClose?: () => void;
  getFloatingProps?: ReturnType<typeof useInteractions>['getFloatingProps'];
}

const SettingsPanel = ({
  ref,
  options,
  value,
  style,
  onChange,
  getFloatingProps,
}: SettingsPanelProps) => {
  const listRef = useRef<Array<HTMLElement | null>>([]);
  const [activeIndex] = useState<number | null>(null);
  const { refs, context } = useFloating({ open: true });
  const listNavigation = useListNavigation(context, {
    listRef,
    activeIndex,
    virtual: true,
  });
  const { getFloatingProps: getListFloatingProps, getItemProps } = useInteractions([
    listNavigation,
  ]);

  const handleSelectOption = useCallback(
    (option: Option) => {
      const val = value ?? [];
      const newValue = val.includes(option.value)
        ? val.filter((v) => v !== option.value)
        : [...val, option.value];
      onChange(newValue);
    },
    [value, onChange],
  );

  return (
    <FloatingList elementsRef={listRef}>
      <div
        ref={ref}
        className={styles.panel}
        style={style}
        {...(getFloatingProps ? getFloatingProps() : {})}>
        <div
          ref={refs.setFloating}
          className={styles.items}
          role="listbox"
          {...getListFloatingProps()}>
          <ItemList
            options={options}
            onClick={(option) => handleSelectOption(option)}
            getItemProps={getItemProps}
            activeIndex={activeIndex}
            value={value}
          />
        </div>
      </div>
    </FloatingList>
  );
};

type ItemListProps = {
  options: SelectOptions;
  value?: string | string[];
  activeIndex: number | null;
  onClick: (item: Option) => void;
  getItemProps: ReturnType<typeof useInteractions>['getItemProps'];
};

const ItemList = (props: ItemListProps) => {
  const { options, value } = props;

  return (
    <>
      {options.map((option, i) => {
        if ('options' in option) {
          if (option.options.length === 0) {
            return null;
          }
          return (
            <Fragment key={i}>
              <div className={styles.title}>{option.label}</div>
              <ItemList
                options={option.options}
                onClick={props.onClick}
                activeIndex={props.activeIndex}
                getItemProps={props.getItemProps}
                value={props.value}
              />
            </Fragment>
          );
        }
        const isSelected = Array.isArray(value)
          ? value.includes(option.value)
          : option.value === value;

        if ('tooltip' in option) {
          return (
            <Tooltip key={i} content={option.tooltip} placement="right">
              <Item
                option={option}
                onClick={props.onClick}
                selected={isSelected}
                activeIndex={props.activeIndex}
                getItemProps={props.getItemProps}
              />
            </Tooltip>
          );
        }

        return (
          <Item
            key={i}
            option={option}
            onClick={props.onClick}
            selected={isSelected}
            activeIndex={props.activeIndex}
            getItemProps={props.getItemProps}
          />
        );
      })}
    </>
  );
};

type ItemProps = {
  ref?: Ref<HTMLButtonElement>;
  option: Option;
  selected?: boolean;
  activeIndex: number | null;
  onClick: (option: Option) => void;
  getItemProps: ReturnType<typeof useInteractions>['getItemProps'];
};

const Item = ({ ref, option, activeIndex, getItemProps, onClick, selected = false }: ItemProps) => {
  const { index } = useListItem();

  return (
    <button
      type="button"
      role="option"
      aria-selected={selected}
      title={'title' in option ? option.title : option.label}
      className={classNames(styles.item, {
        [styles.selected]: selected,
        [styles.active]: index === activeIndex,
      })}
      disabled={option.disabled}
      ref={ref}
      {...getItemProps({
        onClick: () => onClick(option),
      })}>
      {option.label}
      {option.badge !== undefined && (
        <Badge label={option.badge} textColor={badgeColor} bgColor={badgeBgColor} size="small" />
      )}
      {selected && <Icon size={16} name="Check" className={styles.check} />}

      {option.description !== undefined && (
        <span className={styles.description}>{option.description}</span>
      )}
    </button>
  );
};

interface ChatSettingsProps {
  selectedSources: string[];
  selectedMode: ChatMode;
  className?: classNames.Argument;
  size?: FormInputSize;
  placement?: Placement;
  onChange: ({ mode, sources }: { mode: ChatMode; sources: string[] }) => void;
  onClose: () => void;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

export const ChatSettings = ({
  selectedSources,
  selectedMode,
  className,
  size = 'regular',
  placement = 'bottom-start',
  onChange,
  onClose,
  isOpen,
  onOpenChange,
}: ChatSettingsProps) => {
  const { models } = useMetadataContext();
  const account = useSelectedAccount();
  const sources = useMemo(() => {
    const availableSources = getAvailableSources(account.integrations, models);
    const uniqueIntegrations = uniqBy(account.integrations, 'service');
    return [
      DatabaseSource,
      ...uniqueIntegrations.map((integration) => ({
        label:
          integration.service === 'custom_unstructured' && notNil(integration.name)
            ? integration.name
            : toTitleCase(integration.service),
        value: integration.service,
        tooltip: integration.description ?? undefined,
      })),
    ].filter((source) => availableSources.includes(source.value));
  }, [account.integrations, models]);
  const options = useMemo(() => {
    return [
      {
        label: 'Mode',
        options: [NormalMode, ResearchMode],
      },
      {
        label: 'Data sources',
        options: sources,
      },
    ];
  }, [sources]);

  const value = useMemo(
    () => [...selectedSources, selectedMode === 'research' ? 'mode:research' : 'mode:normal'],
    [selectedSources, selectedMode],
  );

  const formattedLabel = useMemo(() => {
    const selected = selectedSources ?? [];
    const isAllSources = selected.length === sources.length;

    if (selected.length === 0) {
      return 'No sources';
    }

    if (selectedMode === 'normal') {
      if (selected.length === 1) {
        return sources.find((source) => source.value === selected[0])?.label ?? selected[0];
      }
      if (isAllSources) {
        return 'All sources';
      }
      return `${selected.length} sources`;
    }

    const count = selected.length !== 0 && !isAllSources ? ` (${selected.length})` : '';
    return `${capitalize(selectedMode)} ${count}`;
  }, [selectedMode, selectedSources, sources]);

  const handleChange = (values: string[]) => {
    const selectedModes = values.filter((value) => value.startsWith('mode:'));
    const researchEnabled =
      selectedModes.length > 0
        ? selectedModes.at(-1) === ResearchMode.value
        : value.includes('mode:research');
    const newSources = values.filter((value) => !value.startsWith('mode:'));

    const settings = {
      mode: researchEnabled ? ('research' as const) : ('normal' as const),
      sources: newSources,
    };
    onChange(settings);
  };

  return (
    <SettingsDropdown
      value={value}
      options={options}
      customLabel={formattedLabel}
      className={className}
      size={size}
      placement={placement}
      onChange={handleChange}
      onClose={onClose}
      isOpen={isOpen}
      onOpenChange={onOpenChange}
    />
  );
};
